import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../feature-name";

/**
 * Runs the Patriot connection check (`patriot-data-service/connection-check.ts`)
 * and returns its step report: whether PDS can reach Patriot, sign in, and
 * read every lane. Support-only.
 *
 * Declared here rather than imported from the check so the wire contract is
 * this module's, per docs/api.practices.md. `step` stays a plain string: the
 * page renders whatever steps the check reports.
 *
 * Undeclared fields are kept (as JSON), so a field the check adds reaches the
 * page's raw view rather than being stripped on the way.
 */
export const PatriotConnectionCheckStepResult = z
  .object({
    step: z.string(),
    ok: z.boolean(),
    durationMs: z.number(),
    detail: z.string(),
  })
  .catchall(z.json());
export type PatriotConnectionCheckStepResult = z.output<typeof PatriotConnectionCheckStepResult>;

export const RunPatriotConnectionCheckResponse = z
  .object({
    ok: z.boolean(),
    /** The first step that failed, or `null` when every step passed. */
    failedAt: z.string().nullable(),
    /** Steps that passed without proving anything: no rows to read, or out of time. */
    unproven: z.array(z.string()),
    steps: z.array(PatriotConnectionCheckStepResult),
  })
  .catchall(z.json());
export type RunPatriotConnectionCheckResponse = z.output<typeof RunPatriotConnectionCheckResponse>;

/**
 * Under `commands/`: running it acts on Patriot rather than reading the
 * replica. The path's tenant plays no part.
 */
export const patriotConnectionCheckResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/commands/connection-check",
  scope: "tenant",
});

/** A POST because each run calls Patriot; the body is empty. */
export const runPatriotConnectionCheckEndpoint = defineEndpoint(patriotConnectionCheckResource, {
  method: "POST",
  bodySchema: z.object({}),
  responseSchema: RunPatriotConnectionCheckResponse,
});
